<template lang="pug">
  CrmLeadReminderListUi(
    v-if="ready"
    :items="rows"
    :draft="draft"
    :saving="saving"
    @add="add"
    @edit="edit"
    @cancel="cancel"
    @save="save"
    @remove="remove"
    )
</template>

<script>
import crmModel from '@/model/crm';
import popupModel from '@/model/popup';
import utils from '@/utils/common';

import crmLeadApi from '@/api/crmLead';
import crmLeadReminderApi from '@/api/crmLeadReminder';

import CrmLeadReminderListUi from '@/components/crm/CrmLeadReminderListUi.vue';

/**
 * Напоминания по заявке: список, добавление, перенос и удаление.
 *
 * Напоминание всегда личное — сервер отдаёт только напоминания текущего
 * пользователя, поля «кому» у него нет (в отличие от напоминаний по задачам).
 * Сработавшие остаются в списке как история и показываются приглушённо.
 */
export default {
  name: 'CrmLeadReminderListApi',
  components: { CrmLeadReminderListUi },
  props: {
    leadId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      reminders: [],
      ready: false,
      // редактируемая строка: id напоминания (null у новой) и выбранное
      // время. Одна на весь список — вторую форму открыть нельзя, пока не
      // сохранена или не отменена первая
      draft: null,
      saving: null,
    };
  },
  created() {
    this.load();
  },
  computed: {
    /**
     * Строки для вёрстки: ближайшие несработавшие сверху, сработавшие — под
     * ними как история. Порядок с сервера не оговорён, поэтому сортируем сами.
     */
    rows() {
      return [...this.reminders]
        .sort((a, b) => (a.isFulfilled - b.isFulfilled) || (new Date(a.notifyAt) - new Date(b.notifyAt)))
        .map(reminder => ({
          id: reminder.id,
          notifyAt: utils.convertDateToString(new Date(reminder.notifyAt), 'long', true),
          isFulfilled: reminder.isFulfilled,
        }));
    },
  },
  methods: {
    async load() {
      const res = await crmModel.run(
        () => crmLeadReminderApi.fetchList({ lead: this.leadId }, true),
        'Не удалось загрузить напоминания',
      );

      this.reminders = res?.items ?? [];
      this.ready = true;
    },

    add() {
      this.draft = { id: null, notifyAt: null };
    },

    edit(row) {
      const reminder = this.reminders.find(item => item.id === row.id);
      if (!reminder) return;

      this.draft = { id: reminder.id, notifyAt: new Date(reminder.notifyAt) };
    },

    cancel() {
      this.draft = null;
    },

    /**
     * Создание или перенос. При переносе сбрасываем isFulfilled тем же
     * запросом: иначе уже сработавшее напоминание так и останется
     * выполненным и по новому времени не придёт.
     *
     * После успешного запроса правим у себя только то, что отправили; ответ
     * на POST кладём целиком — это первое появление напоминания у нас.
     */
    async save(notifyAt) {
      if (!this.draft || !notifyAt) return;

      const { id } = this.draft;
      this.saving = true;

      const result = await crmModel.run(
        () => (id
          ? crmLeadReminderApi.update(id, { notifyAt, isFulfilled: false })
          : crmLeadReminderApi.create({ lead: crmLeadApi.getIri(this.leadId), notifyAt })),
        'Не удалось сохранить напоминание',
      );

      if (result === null) {
        this.saving = null;
        return;
      }

      this.reminders = id
        ? this.reminders.map(item => (item.id === id ? { ...item, notifyAt, isFulfilled: false } : item))
        : [...this.reminders, result];
      this.draft = null;
      this.saving = false;
    },

    async remove(row) {
      const confirmed = await popupModel.addConfirm('Удалить напоминание?', row.notifyAt);
      if (!confirmed) return;

      this.saving = true;

      const result = await crmModel.run(
        () => crmLeadReminderApi.delete(row.id),
        'Не удалось удалить напоминание',
      );

      if (result === null) {
        this.saving = null;
        return;
      }

      this.reminders = this.reminders.filter(item => item.id !== row.id);
      this.saving = false;
    },
  },
};
</script>
